import { PHASE } from "../../../common/constants.ts";
import type { Player, Team } from "../../../common/types.ts";
import getLuxuryTaxAmount from "../finances/getLuxuryTaxAmount.ts";
import getMinPayrollAmount from "../finances/getMinPayrollAmount.ts";

// What a payroll really costs the club in a season, with the game's own rules: a club
// under the minimum pays the difference anyway, one over the luxury line pays the tax.
// Later seasons skip the floor: their payroll is low only because those contracts are
// not signed yet, and the club will fill it.
export const payrollCost = (payroll: number, currentSeason = true) =>
	payroll +
	(currentSeason ? getMinPayrollAmount(payroll) : 0) +
	getLuxuryTaxAmount(payroll);

// Financial contract market for AI clubs. Monitoring (monthly, cheap, persisted on the
// team) is separate from execution (only in the pre-season, mid-season and deadline
// windows). Evaluating a contract never forces an offer: the club keeps the player
// whenever the sporting loss outweighs what it saves, measured against its own budget.

export type ContractWindow = "pre" | "mid" | "deadline";

export type ContractDecision = {
	decision: "keep" | "shop";
	reason: string;
	// Planned roster points lost if he leaves and the best cheap replacement steps in
	netLoss: number;
	// Salary this season and in later seasons, as a share of the club's payroll plan
	savingsShare: number;
	futureShare: number;
	merit: number;
	// Seller assets (lower-half prospects, later picks) may be added only under real
	// budget pressure; a club with room never pays to move salary
	compensation: boolean;
};

export type ContractWatchEntry = ContractDecision & {
	pid: number;
	problemScore: number;
	marketRatio: number;
	// Planned roster points lost without him, before any replacement
	impact: number;
	recentWar: number;
	remainingYears: number;
	month: string;
	// Context of the last real market attempt; an identical context is not retried
	tried?: string;
	result?: string;
};

export type ContractWatch = {
	season: number;
	month: string;
	entries: Record<number, ContractWatchEntry>;
	// Market runs per window this season (computational budget, not a trade limit)
	runs: Record<string, number>;
	// Financial trades completed this season (older saves: missing, counted as 0)
	dumps?: number;
};

export type WatchedTeam = Team & { aiContractWatch?: ContractWatch };

// Same thresholds the roster code already uses: 1.5 problem score flags a contract,
// 32 planned-roster points is the most a club gives up to move salary.
export const PROBLEM_SCORE = 1.5;
export const MAX_NET_LOSS = 32;
export const MERIT_TO_SHOP = 1.5;
// A financial trade must remove at least this share of the player's remaining salary
export const MIN_SAVINGS_SHARE = 0.25;
// Calendar of the market, in regular season schedule days
export const MONITOR_DAYS = 30;
export const DEADLINE_WINDOW_DAYS = 21;
export const RESALE_PROTECTION_DAYS = 60;
// Market runs a club may spend in one window: the regular run (top 2 contracts) plus
// re-runs only for a tried contract whose context changed
export const MAX_WINDOW_RUNS = 3;
// Reference, not a ban: about one salary dump per selling club per season. More need a
// proven financial reason (payroll above the club's plan).
export const DUMPS_PER_SEASON = 1;
export const extraDumpAllowed = (dumps: number, payroll: number, budget: number) =>
	dumps < DUMPS_PER_SEASON || payroll > budget;
// In-season windows: clubs start their run over the first 10 days of the window (about 3 a day)
export const WINDOW_SPREAD_DAYS = 10;
// Salary still owed below 2% of the club's payroll plan is not worth a financial trade
export const MIN_MATERIAL_SHARE = 0.02;
// Compensation is only offered by a club using at least 90% of its payroll plan
export const PRESSURE_PAYROLL = 0.9;

// Monitoring blocks: every 30 schedule days in the regular season; each other phase is one block.
export const contractMonth = (phase: number, day: number) =>
	phase === PHASE.REGULAR_SEASON
		? `R${Math.floor(Math.max(0, day - 1) / MONITOR_DAYS)}`
		: `P${phase}`;

// Pre-season covers the offseason phases where trades are allowed. The deadline window
// opens 21 days before the trade deadline of the actual schedule; mid-season is 40-50%
// of the schedule days, before that. Without a deadline marker only mid-season applies.
export const contractWindow = (
	phase: number,
	seasonFraction: number,
	daysToDeadline: number | undefined,
): ContractWindow | undefined => {
	if (
		phase === PHASE.PRESEASON ||
		phase === PHASE.AFTER_DRAFT ||
		phase === PHASE.RESIGN_PLAYERS ||
		phase === PHASE.FREE_AGENCY
	) {
		return "pre";
	}
	if (phase !== PHASE.REGULAR_SEASON) return undefined;
	if (daysToDeadline !== undefined && daysToDeadline < 0) return undefined;
	if (daysToDeadline !== undefined && daysToDeadline <= DEADLINE_WINDOW_DAYS) {
		return "deadline";
	}
	if (seasonFraction >= 0.4 && seasonFraction < 0.5) return "mid";
	return undefined;
};

// Position on a single timeline: season * 1000 + regular season day. Offseason and
// pre-season trades count as day 0 of the season that follows them.
export const dayStamp = (season: number, phase: number, day = 0) =>
	(phase >= PHASE.PLAYOFFS ? season + 1 : season) * 1000 +
	(phase === PHASE.REGULAR_SEASON || phase === PHASE.AFTER_TRADE_DEADLINE
		? day
		: 0);

// A player traded in the last 60 schedule days is not shopped again for money (nor used
// as salary coming back), and never goes back to a club that traded him recently.
// p.transactions is saved with the player, so this survives reloading the league.
// Older saves have no day on in-season trades: they count from the start of that season.
export const recentlyTraded = (
	p: Pick<Player, "transactions">,
	now: number,
	days = RESALE_PROTECTION_DAYS,
) =>
	(p.transactions ?? []).some((t) => {
		if (t.type !== "trade") return false;
		const elapsed =
			now - dayStamp(t.season, t.phase, (t as { day?: number }).day ?? 0);
		return elapsed >= 0 && elapsed < days;
	});

export const recentFormerTeams = (
	p: Pick<Player, "transactions">,
	season: number,
	seasons = 2,
) =>
	new Set(
		(p.transactions ?? [])
			.filter(
				(t) =>
					t.type === "trade" &&
					t.season >= season - seasons &&
					t.fromTid !== undefined,
			)
			.map((t) => (t as { fromTid: number }).fromTid),
	);

// What changes the market: the window itself (closer to the deadline, other clubs'
// needs re-read), the player's health and the club's budget pressure. The seller's own
// roster count is left out: a spot opened by its own trade is not a new market.
export const contractContext = (input: {
	window: ContractWindow;
	injured: boolean;
	overBudget: boolean;
	pressure: boolean;
}) =>
	[
		input.window,
		input.injured ? "inj" : "ok",
		input.overBudget ? "over" : input.pressure ? "tight" : "plan",
	].join("|");

// Trade or keep. The sporting cost is the loss after the best cheap replacement, the
// financial gain is salary relative to the club's own payroll plan (so the league's
// salary scale never matters), and a contract that is merely expensive stays.
export const decideContract = (input: {
	problemScore: number;
	impact: number;
	netLoss: number;
	// Effective saving this season (salary, minimum payroll top-up, luxury tax)
	salary: number;
	remainingYears: number;
	budget: number;
	payroll: number;
	// Effective saving in the later contract years; defaults to the same every year
	futureSavings?: number;
}): ContractDecision => {
	const budget = Math.max(1, input.budget);
	const savingsShare = Math.max(0, input.salary) / budget;
	const futureShare =
		Math.max(
			0,
			input.futureSavings ??
				input.salary * Math.max(0, input.remainingYears - 1),
		) / budget;
	const overBudget = input.payroll > budget;
	const pressure =
		savingsShare * 4 * (overBudget ? 1.5 : 1) + Math.min(1, futureShare * 2);
	const merit = input.problemScore + pressure - input.netLoss / 12;
	const compensation = input.payroll >= budget * PRESSURE_PAYROLL;
	const base = { netLoss: input.netLoss, savingsShare, futureShare, merit, compensation };
	if (input.problemScore < PROBLEM_SCORE) {
		return { ...base, decision: "keep", reason: "contrato coerente com o mercado e o papel" };
	}
	if (savingsShare + futureShare < MIN_MATERIAL_SHARE) {
		return { ...base, decision: "keep", reason: "economia efetiva pequena para o orçamento (salário, piso da folha e imposto)" };
	}
	if (input.netLoss > MAX_NET_LOSS) {
		return { ...base, decision: "keep", reason: "útil demais: sem substituto barato à altura" };
	}
	if (merit < MERIT_TO_SHOP) {
		return { ...base, decision: "keep", reason: "a perda esportiva supera a economia" };
	}
	return { ...base, decision: "shop", reason: "economia relevante com perda esportiva pequena" };
};

// Compensation offered by a seller: only the lower half of its prospects by value (at
// most 4), never the best ones, and at most one extra asset unless the salary still owed
// is at least 5% of the club's plan, when a moderate pair (prospect + pick) is allowed.
export const compensationProspects = <T extends { value: number; pid: number }>(
	prospects: T[],
) =>
	[...prospects]
		.sort((a, b) => a.value - b.value || b.pid - a.pid)
		.slice(0, Math.min(4, Math.floor(prospects.length / 2)));

export const maxCompensationAssets = (materialShare: number) =>
	materialShare >= 0.05 ? 2 : 1;

// The trade actually found is judged again with what really moves: salary back from the
// buyer and the planned roster with the incoming players.
export const judgeFinancialTrade = (input: {
	problemScore: number;
	outgoingRemaining: number;
	netSavingsRemaining: number;
	netSavingsNow: number;
	sportingLoss: number;
	remainingYears: number;
	budget: number;
	payroll: number;
}) => {
	// netSavingsRemaining is effective (minimum payroll and luxury tax included); a club
	// sitting on the payroll floor saves nothing by moving salary
	if (input.netSavingsRemaining < input.outgoingRemaining * MIN_SAVINGS_SHARE) {
		return { ok: false, reason: "economia pequena demais: troca só geraria movimento" };
	}
	const decision = decideContract({
		problemScore: input.problemScore,
		impact: input.sportingLoss,
		netLoss: Math.max(0, input.sportingLoss),
		salary: Math.max(0, input.netSavingsNow),
		futureSavings: Math.max(0, input.netSavingsRemaining - input.netSavingsNow),
		remainingYears: input.remainingYears,
		budget: input.budget,
		payroll: input.payroll,
	});
	return decision.decision === "shop"
		? { ok: true, reason: decision.reason }
		: { ok: false, reason: `pacote real: ${decision.reason}` };
};
